/**
 * FileDropZone — generic drag-drop surface with built-in validation.
 *
 * Validates ALL files (not just the first like the old DropZonePanel) against
 * extension/size/count constraints. If ANY file fails, NO file is accepted —
 * the whole drop is cancelled and a toast explains why. This "all-or-nothing"
 * behaviour avoids silent partial uploads.
 *
 * Backward compat: the default export is still `DropZonePanel` for existing
 * imports, and the named export `FileDropZone` is the new preferred name.
 *
 * @example Simple image drop:
 * <FileDropZone
 *   accept="image"
 *   maxSize={MAX_SIZE_IMAGE}
 *   onFileDrop={(files) => upload(files)}
 * />
 *
 * @example With a file list as children:
 * <FileDropZone accept="any" onFileDrop={handleDrop}>
 *   {items.length > 0 && <ul>{items.map(...)}</ul>}
 * </FileDropZone>
 */
import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import {
  validateFiles,
  summarizeRejections,
  type AcceptMode,
} from '../lib/fileValidation';

export interface FileDropZoneProps {
  /** Called with the accepted files. Only invoked if ALL files pass validation. */
  onFileDrop: (files: File[]) => void;
  /** Kind of files expected. Default 'any'. */
  accept?: AcceptMode;
  /** Max size per file in bytes. Defaults based on `accept`. */
  maxSize?: number;
  /** Max total files (existing + new). Omit for unlimited. */
  maxCount?: number;
  /** Number of files already present — for maxCount enforcement. */
  existingCount?: number;
  /** Override the allowed extension list. */
  allowedExtensions?: readonly string[];
  /** Visual variant: 'full' (large, centered), 'compact' (thin inline). */
  variant?: 'full' | 'compact';
  /** Accent colour for drag-over state. Default 'primary'. */
  color?: 'primary' | 'blue';
  /** Material Symbols icon for empty state. Default 'cloud_upload'. */
  icon?: IconName;
  /** Empty-state label. Default "Arrastra archivos aquí o haz clic". */
  label?: string;
  /** Force red error state (e.g. form validation error). */
  hasError?: boolean;
  className?: string;
  /** If provided, rendered instead of the empty-state (e.g. a file list). */
  children?: React.ReactNode;
  /** Click handler (usually triggers hidden input[type=file]).
   *
   * Behaviour:
   * - Empty state (no children): the whole dropzone is clickable → opens
   *   the file picker.
   * - With children (a grid/list): the outer area is NOT clickable.
   *   Instead, an explicit "+" upload button is rendered absolute
   *   top-right and triggers ``onClick``. This avoids accidentally
   *   opening the picker when interacting with the rendered content. */
  onClick?: () => void;
  /** Label of the built-in upload button shown when children are
   *  present. Defaults to "Añadir". */
  uploadButtonLabel?: string;
}

const dragValidClass: Record<NonNullable<FileDropZoneProps['color']>, string> = {
  primary: 'bg-primary/10 border-primary border-dashed scale-[1.02]',
  blue: 'bg-blue-500/10 border-blue-500 border-dashed scale-[1.02]',
};

// Idle state always uses dashed borders so the drop affordance is visible
// even without dragging — matches the "drag-and-drop field" visual language
// (see Standards.md §"File drop zones").
const idleClass = 'bg-white dark:bg-surface-dark border-border-strong dark:border-border-strong-dark border-dashed shadow-sm';
const errorClass = 'bg-red-500/10 border-red-500 border-dashed scale-[1.02]';

const FileDropZone: React.FC<FileDropZoneProps> = ({
  onFileDrop,
  accept = 'any',
  maxSize,
  maxCount,
  existingCount,
  allowedExtensions,
  variant = 'full',
  color = 'primary',
  icon = 'cloud_upload',
  label,
  hasError = false,
  className = '',
  children,
  onClick,
  uploadButtonLabel,
}) => {
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [isDragError, setIsDragError] = useState(false);

  // Cheap drag-enter validation — mime type of each item
  const allDragItemsValid = (items: DataTransferItemList): boolean => {
    if (accept === 'any') return true;
    if (items.length === 0) return true;
    if (accept === 'image' || accept === 'logo') {
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it && !it.type.startsWith('image/')) return false;
      }
      return true;
    }
    if (accept === 'design3d' || accept === 'document') {
      // Only reject obvious images when expecting non-images
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it && it.type.startsWith('image/')) return false;
      }
      return true;
    }
    return true;
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const valid = allDragItemsValid(e.dataTransfer.items);
    setIsDragging(true);
    setIsDragError(!valid);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Critical guard: prevent flicker when cursor moves across child elements
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
    setIsDragError(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setIsDragError(false);
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;

    const files = Array.from(e.dataTransfer.files);
    const result = validateFiles(files, { accept, maxSize, maxCount, existingCount, allowedExtensions });

    if (!result.allValid) {
      toast.error(summarizeRejections(result.rejected));
      return; // all-or-nothing: cancel the whole drop
    }

    onFileDrop(result.accepted);
  };

  const showError = isDragError || hasError;
  const borderClass = showError
    ? errorClass
    : isDragging
      ? dragValidClass[color]
      : idleClass;

  const padding = variant === 'compact' ? 'p-3' : 'p-4';
  // Named group scope (`group/dropzone`) so the hover styles below only react
  // to the dropzone itself, not any ancestor with an unnamed `group` class
  // (e.g. OrderItemCard wraps its content in `group` for other micro-interactions).
  // 2 px dashed border so the pattern reads clearly — matches `DropZoneButton`.
  const finalClass = `group/dropzone ${padding} rounded-xl border-2 border-dashed transition-all duration-200 ${borderClass} ${className}`.trim();

  // If children are provided, render them. Otherwise show the default empty state.
  const showEmptyState = !children;
  const iconSize = variant === 'compact' ? '!text-[20px]' : '!text-[32px]';
  const labelText = label ?? 'Subir archivos';
  const minHEmpty = variant === 'compact' ? 'min-h-[60px]' : 'min-h-[120px]';
  const hoverIconColor = color === 'blue' ? 'group-hover/dropzone:text-blue-500' : 'group-hover/dropzone:text-primary';
  const hoverLabelColor = color === 'blue' ? 'group-hover/dropzone:text-blue-500' : 'group-hover/dropzone:text-primary';

  // Click-to-upload semantics:
  // - Empty state → outer div is the trigger (large affordance, like an
  //   uploader card).
  // - With children → outer div is NOT clickable; the explicit "+ Añadir"
  //   button at top-right is the only trigger. Avoids accidental file
  //   pickers when the user clicks on a thumbnail.
  const outerClickable = !!onClick && showEmptyState;
  const showUploadButton = !!onClick && !showEmptyState;

  return (
    <div
      className={finalClass}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={outerClickable ? onClick : undefined}
      role={outerClickable ? 'button' : undefined}
      tabIndex={outerClickable ? 0 : undefined}
      onKeyDown={outerClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
      aria-label={labelText}
    >
      {showEmptyState ? (
        <div className={`flex flex-col items-center justify-center gap-2 h-full w-full ${minHEmpty} ${onClick ? 'cursor-pointer' : ''}`}>
          <Icon name={icon} className={`${iconSize} text-slate-300 dark:text-slate-600 ${hoverIconColor} transition-colors`} />
          <p className={`text-xs font-bold text-slate-400 uppercase tracking-wider text-center ${hoverLabelColor} transition-colors`}>{labelText}</p>
        </div>
      ) : (
        <>
          {showUploadButton && (
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClick?.(); }}
                title={uploadButtonLabel ?? 'Añadir'}
                aria-label={uploadButtonLabel ?? 'Añadir'}
                className="p-1 bg-slate-100 dark:bg-surface-dark text-slate-600 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <Icon name="upload_file" className="!text-[16px]" />
              </button>
            </div>
          )}
          {children}
        </>
      )}
    </div>
  );
};

// Keep old name as alias for backward compatibility.
export { FileDropZone };
export default FileDropZone;
